'use client';

import { ScrollArea as ScrollAreaPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '../lib/utils';

type ScrollAreaProps = React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  hideScrollbar?: boolean;
};

function ScrollArea({ className, children, hideScrollbar = false, ...props }: ScrollAreaProps) {
  return (
    <ScrollAreaPrimitive.Root data-slot="scroll-area" className={cn('group/scroll-area relative flex flex-col overflow-hidden', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        className={cn(
          'flex-1 w-full rounded-[inherit] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1',
          '[&>div]:!block [&>div]:min-w-0 [&>div]:w-full',
          hideScrollbar ? 'hide-scrollbar' : 'transition-[color,box-shadow,padding-right] group-has-data-[state=visible]/scroll-area:pr-3'
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar className={hideScrollbar ? 'hidden' : undefined} />
      <ScrollAreaPrimitive.Corner className={hideScrollbar ? 'hidden' : undefined} />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({ className, orientation = 'vertical', ...props }: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        'flex touch-none select-none transition-colors',
        'data-[state=hidden]:opacity-0 data-[state=visible]:opacity-100',
        orientation === 'vertical' && 'absolute right-0 top-0 h-full w-2.5 border-l border-l-transparent p-px',
        orientation === 'horizontal' && 'absolute bottom-0 left-0 h-2.5 w-full flex-col border-t border-t-transparent p-px',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb data-slot="scroll-area-thumb" className="relative flex-1 rounded-full bg-border" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

export { ScrollArea, ScrollBar };
